import { Injectable, type Provider } from '@angular/core';

import { ok, type ApiResult } from '../../core/models/api-result';
import {
  ContentCommentsService,
  type CommentsResponse,
} from './content-comments.service';
import type { ThreadComment } from './comment-thread/thread-tree';

/**
 * A CONVERSATION, WITHOUT A SESSION.
 *
 * Same rule as `HubFeedPreviewService`: override ONE seam — `fetch` — and
 * inherit the keyed cache, the paging, the append and the optimistic add from
 * the real service, so `/preview/hubs` exercises the shipping code rather than
 * a lookalike.
 *
 * ── THE THREADS ARE DERIVED, NOT TRANSCRIBED ──────────────────────────────
 * Sixty-two posts would need sixty-two hand-written threads to be transcribed,
 * and the point of the harness is to review the RAIL — nesting, the depth cap,
 * ordering, the reply affordance, an empty state — not to read the fiction. So
 * the shape is generated deterministically from the post id: the same post
 * always gets the same thread, across reloads and across screenshots, which is
 * what makes a visual diff mean anything.
 *
 * Deliberately included, because each is a distinct rendering problem:
 *   · a post with NO comments at all (the empty rail),
 *   · replies nested past the two-indent cap,
 *   · a very long single comment, and a one-word one,
 *   · a comment the viewer has already liked.
 */

const AUTHORS = [
  { id: 'sana-iqbal', name: 'Sana Iqbal' },
  { id: 'tom-whitfield', name: 'Tom Whitfield' },
  { id: 'grace-oyelaran', name: 'Grace Oyelaran' },
  { id: 'marek-nowak', name: 'Marek Nowak' },
  { id: 'priya-raman', name: 'Priya Raman' },
  { id: 'callum-rees', name: 'Callum Rees' },
];

const OPENERS = [
  'This is the first I have heard of it and I am on that floor every day.',
  'Genuinely useful, thank you for writing it up.',
  'Does this apply to the Leeds office too, or just here?',
  'Seconded.',
  'I have been asking for this for about two years, so: at last.',
  'One thing worth adding — the side entrance is closed until the tenth, so the walk round is longer than it looks on the map.',
  'Is there a recording for anyone who could not make it?',
];

const REPLIES = [
  'Both, as I understand it.',
  'There is — I will drop the link in here once it has finished processing.',
  'Same experience on the third floor.',
  'That was my reading of it as well, but I would not swear to it.',
  'Ah, that explains the queue this morning.',
];

/** Deterministic, so the same post always gets the same thread. */
function seedOf(text: string): number {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  }
  return hash;
}

const BASE_TIME = Date.parse('2026-08-21T09:00:00.000Z');

function threadFor(contentId: string): ThreadComment[] {
  const seed = seedOf(contentId);
  // One post in seven has no conversation at all. The empty rail is a state
  // that has to be looked at, and a corpus where everything has comments is a
  // corpus in which it never renders.
  if (seed % 7 === 0) return [];

  const rootCount = 1 + (seed % 4);
  const out: ThreadComment[] = [];

  for (let r = 0; r < rootCount; r++) {
    const rootId = `${contentId}-c${r}`;
    const author = AUTHORS[(seed + r) % AUTHORS.length];
    out.push({
      _id: rootId,
      userId: author.id,
      name: author.name,
      image: null,
      text: OPENERS[(seed + r * 3) % OPENERS.length],
      parentId: null,
      createdAt: new Date(BASE_TIME - (r + 1) * 3_600_000).toISOString(),
      likeCount: (seed + r) % 9,
      viewerLiked: (seed + r) % 5 === 0,
    });

    const replyCount = (seed + r) % 3;
    let parentId = rootId;
    for (let i = 0; i < replyCount; i++) {
      const replyAuthor = AUTHORS[(seed + r + i + 2) % AUTHORS.length];
      const id = `${rootId}-r${i}`;
      out.push({
        _id: id,
        userId: replyAuthor.id,
        name: replyAuthor.name,
        image: null,
        text: REPLIES[(seed + i) % REPLIES.length],
        // CHAINED, not flat. Every reply answers the one before it, so the
        // third is at depth 3 and the two-indent cap has something to catch —
        // a corpus of depth-1 replies never exercises the rule.
        parentId,
        createdAt: new Date(
          BASE_TIME - (r + 1) * 3_600_000 + (i + 1) * 600_000,
        ).toISOString(),
        likeCount: (seed + i) % 4,
        viewerLiked: false,
      });
      parentId = id;
    }
  }

  return out;
}

@Injectable()
export class ContentCommentsPreviewService extends ContentCommentsService {
  protected override async fetch(
    contentId: string,
    after: string | null,
  ): Promise<ApiResult<CommentsResponse | null>> {
    const all = threadFor(contentId);
    const start = after ? Number(after) : 0;
    const slice = all.slice(start, start + 20);
    const next = start + slice.length;

    // The `contentItem` ITSELF, because that is what the real `fetch` returns:
    // `queryResult` has already unwrapped the root field by the time either of
    // them hands anything back. See the note on `CommentsResponse`.
    return ok({
      id: contentId,
      commentCount: all.length,
      comments: {
        totalCount: all.length,
        pageInfo: {
          endCursor: next < all.length ? String(next) : null,
          hasNextPage: next < all.length,
        },
        edges: slice.map((node, i) => ({ cursor: String(start + i), node })),
      },
    });
  }

  /**
   * The append, without a server. The real one returns the SAVED row so the
   * comment can be replied to immediately; this mints the equivalent, and the
   * inherited `add` does the rest.
   */
  override async add(
    contentId: string,
    text: string,
    parentId: string | null,
  ): Promise<boolean> {
    const body = text.trim();
    if (!contentId || !body) return false;

    const current = this.threadFor()(contentId);
    this.patch(contentId, {
      comments: [
        ...current.comments,
        {
          _id: `${contentId}-local-${current.comments.length}`,
          userId: 'you',
          name: 'You',
          image: null,
          text: body,
          parentId,
          createdAt: new Date(BASE_TIME).toISOString(),
          likeCount: 0,
          viewerLiked: false,
        },
      ],
      totalCount: current.totalCount + 1,
      error: null,
    });
    return true;
  }

  /** The heart flips and the count moves; there is no server to settle it. */
  override async toggleLike(contentId: string, commentId: string): Promise<boolean> {
    const current = this.threadFor()(contentId);
    this.patch(contentId, {
      comments: current.comments.map(c =>
        c._id === commentId
          ? {
              ...c,
              viewerLiked: !c.viewerLiked,
              likeCount: Math.max(0, (c.likeCount ?? 0) + (c.viewerLiked ? -1 : 1)),
            }
          : c,
      ),
    });
    return true;
  }

  /** The row goes; nothing refuses it. */
  override async remove(contentId: string, commentId: string): Promise<boolean> {
    const current = this.threadFor()(contentId);
    if (!current.comments.some(c => c._id === commentId)) return false;
    this.patch(contentId, {
      comments: current.comments.filter(c => c._id !== commentId),
      totalCount: Math.max(0, current.totalCount - 1),
    });
    return true;
  }
}

export function provideHubsCommentsPreview(): Provider {
  return { provide: ContentCommentsService, useClass: ContentCommentsPreviewService };
}
